import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { ArrowLeft, ArrowRight, Save, AlertCircle, X, Check } from "lucide-react";

const TIMEZONES = [
  "America/Sao_Paulo", "America/Manaus", "America/Belem", "America/Fortaleza",
  "America/Bahia", "America/Recife", "America/Cuiaba", "America/Campo_Grande",
  "America/Porto_Velho", "America/Boa_Vista", "America/Rio_Branco", "America/Noronha"
];

const STATES = [
  "AC","AL","AP","AM","BA","CE","DF","ES","GO","MA","MT","MS","MG","PA",
  "PB","PR","PE","PI","RJ","RN","RS","RO","RR","SC","SP","SE","TO"
];

const PERSON_TYPES = [
  { value: "individual", label: "Pessoa Fisica (CPF)" },
  { value: "legal_entity", label: "Pessoa Juridica (CNPJ)" }
];

const TAX_REGIMES = [
  "Simples Nacional",
  "Lucro Presumido",
  "Lucro Real",
  "MEI",
  "Isento"
];

const STEPS = [
  { id: 1, label: "Identificacao" },
  { id: 2, label: "Dados Fiscais" },
  { id: 3, label: "Contato" },
  { id: 4, label: "Endereco" },
  { id: 5, label: "Configuracoes" }
];

const validateStep = (step, form) => {
  const errors = {};
  if (step === 1) {
    if (!form.name || form.name.trim().length < 2) {
      errors.name = "Nome é obrigatório (mínimo 2 caracteres)";
    }
    if (!form.document || form.document.trim().length < 2) {
      errors.document = "Documento é obrigatório";
    }
  }
  if (step === 2) {
    if (!form.legalName || form.legalName.trim().length < 2) {
      errors.legalName = "Razão Social é obrigatória";
    }
    if (!form.taxRegime || form.taxRegime.trim() === "") {
      errors.taxRegime = "Regime Tributário é obrigatório";
    }
  }
  if (step === 3) {
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) {
      errors.email = "E-mail inválido";
    }
  }
  if (step === 4) {
    if (!form.postalCode || form.postalCode.trim() === "") {
      errors.postalCode = "CEP é obrigatório";
    }
    if (!form.street || form.street.trim().length < 2) {
      errors.street = "Logradouro é obrigatório";
    }
    if (!form.number || form.number.trim() === "") {
      errors.number = "Número é obrigatório";
    }
    if (!form.city || form.city.trim() === "") {
      errors.city = "Cidade é obrigatória";
    }
    if (!form.state || form.state.trim() === "") {
      errors.state = "Estado é obrigatório";
    }
  }
  if (step === 5) {
    return errors;
  }
  return errors;
};

const CompanyWizard = ({ initialData, loading, error, onSubmit, submitLabel = "Salvar", backPath = "/companies" }) => {
  const history = useHistory();
  const [step, setStep] = useState(1);
  const [stepErrors, setStepErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);
  const [synced, setSynced] = useState(false);
  const [form, setForm] = useState({
    name: "", document: "", personType: "legal_entity",
    legalName: "", tradeName: "",
    email: "", phone: "", fiscalEmail: "",
    postalCode: "", street: "", number: "", complement: "",
    district: "", city: "", state: "",
    municipalRegistration: "", stateRegistration: "", taxRegime: "", cnae: "",
    timezone: "America/Sao_Paulo", internalNotes: "",
    responsibleName: "", responsibleEmail: "", responsiblePhone: ""
  });
  const [isNavigating, setIsNavigating] = useState(false);

  useEffect(() => {
    if (initialData && !synced) {
      setForm(initialData);
      setSynced(true);
      console.log("Initial data loaded, set synced to true");
    }
  }, [initialData, synced]);

  const showLoading = loading || (initialData && !synced);

  const updateField = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (stepErrors[field]) {
      setStepErrors(prev => { const next = { ...prev }; delete next[field]; return next; });
    }
  };

  const handleNext = () => {
    console.log(`handleNext chamado. Current step: ${step}, max step: ${STEPS.length}`);
    if (step >= STEPS.length || isNavigating) {
      console.log("Já está na ultima etapa ou navegando: retornando");
      return;
    }
    setIsNavigating(true);
    const errors = validateStep(step, form);
    if (Object.keys(errors).length > 0) {
      console.log("Erros de validacao:", errors);
      setStepErrors(errors);
      setIsNavigating(false);
      return;
    }
    console.log("Validacao passou, indo para proximo passo");
    setStepErrors({});
    setTimeout(() => {
      setIsNavigating(false);
    }, 100);
    setStep(prevStep => {
      const newStep = Math.min(prevStep + 1, STEPS.length);
      console.log(`Nova etapa: ${newStep}`);
      return newStep;
    });
  };

  const handlePrev = () => {
    setStepErrors({});
    setStep(s => Math.max(s - 1, 1));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log("handleSubmit chamado no passo:", step);
    const allErrors = {};
    for (let s = 1; s <= STEPS.length; s++) Object.assign(allErrors, validateStep(s, form));
    if (Object.keys(allErrors).length > 0) {
      console.log("Erro de validacao ao salvar:", allErrors);
      setStepErrors(allErrors);
      if (Object.keys(allErrors).some(k => k === "name")) {
        setStep(1);
      } else if (["legalName", "taxRegime", "municipalRegistration", "stateRegistration", "cnae"].some(k => allErrors[k])) {
        setStep(2);
      } else if (["email", "phone", "fiscalEmail"].some(k => allErrors[k])) {
        setStep(3);
      } else if (["postalCode", "street", "number", "complement", "district", "city", "state"].some(k => allErrors[k])) {
        setStep(4);
      }
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      await onSubmit(form);
      setToast({ type: "success", message: "Salvo com sucesso" });
    } catch (err) {
      setToast({ type: "error", message: err.message || "Erro ao salvar" });
    } finally {
      setSaving(false);
    }
  };

  if (showLoading) {
    return (
      <div className="space-y-4">
        <div className="neumorphic-card rounded-2xl p-6 animate-pulse space-y-3">
          <div className="h-4 bg-gray-200 dark:bg-white/5 rounded w-1/4" />
          <div className="h-4 bg-gray-200 dark:bg-white/5 rounded w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-600 dark:text-rose-300 text-xs font-semibold flex items-center gap-3">
          <AlertCircle size={16} /> {error}
        </div>
      </div>
    );
  }

  const inputClass = "w-full px-3 py-2 bg-gray-50 dark:bg-[#191c1e] border border-gray-200 dark:border-white/10 rounded-xl text-xs text-gray-900 dark:text-white focus:outline-none focus:border-purple-500";
  const inputErrorClass = inputClass + " border-rose-400 dark:border-rose-500/50";

  return (
    <div className="space-y-4">
      {toast && (
        <div className={`p-3 rounded-xl border text-xs font-semibold flex items-center justify-between ${
          toast.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500/20 text-rose-700 dark:text-rose-300"
        }`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="text-gray-400 hover:text-gray-700 dark:hover:text-white"><X size={14} /></button>
        </div>
      )}

      <div className="neumorphic-card rounded-2xl p-4 border border-gray-200 dark:border-white/10">
        <div className="flex items-center justify-between">
          {STEPS.map((s, idx) => (
            <React.Fragment key={s.id}>
              <button type="button" onClick={() => { if (s.id < step) { setStepErrors({}); setStep(s.id); } }}
                className={`flex items-center gap-2 px-2 py-2 rounded-xl text-xs font-bold transition-all ${
                  step === s.id ? "bg-purple-500/10 border border-purple-500/20 text-purple-600 dark:text-purple-400"
                  : s.id < step ? "text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/5 cursor-pointer"
                  : "text-gray-400 dark:text-gray-500 cursor-default"
                }`}>
                {s.id < step ? (
                  <div className="w-5 h-5 rounded-full bg-emerald-500/20 flex items-center justify-center"><Check size={12} /></div>
                ) : (
                  <div className={`w-5 h-5 rounded-full border flex items-center justify-center text-[10px] ${
                    step === s.id ? "border-purple-500 text-purple-500" : "border-gray-300 dark:border-gray-600"
                  }`}>{s.id}</div>
                )}
                <span className="hidden md:inline">{s.label}</span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-px mx-1 ${s.id < step ? "bg-emerald-400/30" : "bg-gray-200 dark:bg-white/5"}`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()} className="space-y-4">
        <div className="neumorphic-card rounded-2xl p-6 border border-gray-200 dark:border-white/10 space-y-6">
          <div className="flex items-center gap-3 pb-4 border-b border-gray-100 dark:border-white/5">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 flex items-center justify-center text-sm font-bold">{step}</div>
            <div>
              <h3 className="text-sm font-bold text-gray-900 dark:text-white">{STEPS[step - 1].label}</h3>
              <p className="text-[11px] text-gray-500 dark:text-gray-400">Etapa {step} de {STEPS.length}</p>
            </div>
          </div>

          <div style={{ display: step === 1 ? "block" : "none" }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Nome da Empresa *</label>
                <input type="text" value={form.name} onChange={(e) => updateField("name", e.target.value)}
                  className={stepErrors.name ? inputErrorClass : inputClass} placeholder="Razao social ou nome fantasia" required minLength={2} />
                {stepErrors.name && <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-1">{stepErrors.name}</p>}
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Documento (CPF/CNPJ)</label>
                <input type="text" value={form.document} onChange={(e) => updateField("document", e.target.value.replace(/\D/g, ""))}
                  className={inputClass} placeholder="Apenas numeros" maxLength={14} />
              </div>

          </div>

          <div style={{ display: step === 2 ? "block" : "none" }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Tipo de Pessoa</label>
                <select value={form.personType} onChange={(e) => updateField("personType", e.target.value)} className={inputClass}>
                  {PERSON_TYPES.map(pt => <option key={pt.value} value={pt.value}>{pt.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Razao Social (Fiscal)</label>
                <input type="text" value={form.legalName} onChange={(e) => updateField("legalName", e.target.value)}
                  className={inputClass} placeholder="Razao social completa" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Nome Fantasia</label>
                <input type="text" value={form.tradeName} onChange={(e) => updateField("tradeName", e.target.value)}
                  className={inputClass} placeholder="Nome fantasia" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Inscricao Municipal</label>
                <input type="text" value={form.municipalRegistration} onChange={(e) => updateField("municipalRegistration", e.target.value)}
                  className={inputClass} placeholder="Numero da inscricao" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Inscricao Estadual</label>
                <input type="text" value={form.stateRegistration} onChange={(e) => updateField("stateRegistration", e.target.value)}
                  className={inputClass} placeholder="Numero da inscricao" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Regime Tributario</label>
                <select value={form.taxRegime} onChange={(e) => updateField("taxRegime", e.target.value)} className={inputClass}>
                  <option value="">Selecione</option>
                  {TAX_REGIMES.map(tr => <option key={tr} value={tr}>{tr}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">CNAE</label>
                <input type="text" value={form.cnae} onChange={(e) => updateField("cnae", e.target.value)}
                  className={inputClass} placeholder="Codigo CNAE" />
              </div>
          </div>

          <div style={{ display: step === 3 ? "block" : "none" }} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">E-mail</label>
                <input type="email" value={form.email} onChange={(e) => updateField("email", e.target.value)}
                  className={stepErrors.email ? inputErrorClass : inputClass} placeholder="email@empresa.com.br" />
                {stepErrors.email && <p className="text-[10px] text-rose-500 dark:text-rose-400 mt-1">{stepErrors.email}</p>}
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Telefone</label>
                <input type="text" value={form.phone} onChange={(e) => updateField("phone", e.target.value)}
                  className={inputClass} placeholder="+5511999999999" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">E-mail Fiscal</label>
                <input type="email" value={form.fiscalEmail} onChange={(e) => updateField("fiscalEmail", e.target.value)}
                  className={inputClass} placeholder="email@empresa.com.br (para NFS-e)" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Nome do Responsavel</label>
                <input type="text" value={form.responsibleName} onChange={(e) => updateField("responsibleName", e.target.value)}
                  className={inputClass} placeholder="Nome completo do responsavel" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">E-mail do Responsavel</label>
                <input type="email" value={form.responsibleEmail} onChange={(e) => updateField("responsibleEmail", e.target.value)}
                  className={inputClass} placeholder="responsavel@empresa.com.br" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Telefone do Responsavel</label>
                <input type="text" value={form.responsiblePhone} onChange={(e) => updateField("responsiblePhone", e.target.value)}
                  className={inputClass} placeholder="+5511999999999" />
              </div>
          </div>

          <div style={{ display: step === 4 ? "block" : "none" }} className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">CEP</label>
                <input type="text" value={form.postalCode} onChange={(e) => updateField("postalCode", e.target.value.replace(/\D/g, ""))}
                  className={inputClass} placeholder="00000000" maxLength={8} />
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Logradouro</label>
                <input type="text" value={form.street} onChange={(e) => updateField("street", e.target.value)}
                  className={inputClass} placeholder="Rua, Avenida, etc." />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Numero</label>
                <input type="text" value={form.number} onChange={(e) => updateField("number", e.target.value)}
                  className={inputClass} placeholder="123" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Complemento</label>
                <input type="text" value={form.complement} onChange={(e) => updateField("complement", e.target.value)}
                  className={inputClass} placeholder="Sala 1, Bloco B" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Bairro</label>
                <input type="text" value={form.district} onChange={(e) => updateField("district", e.target.value)}
                  className={inputClass} placeholder="Centro" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Cidade</label>
                <input type="text" value={form.city} onChange={(e) => updateField("city", e.target.value)}
                  className={inputClass} placeholder="Porto Velho" />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Estado</label>
                <select value={form.state} onChange={(e) => updateField("state", e.target.value)} className={inputClass}>
                  <option value="">Selecione</option>
                  {STATES.map(st => <option key={st} value={st}>{st}</option>)}
                </select>
              </div>
          </div>

          <div style={{ display: step === 5 ? "block" : "none" }} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Timezone</label>
                <select value={form.timezone} onChange={(e) => updateField("timezone", e.target.value)} className={inputClass}>
                  {TIMEZONES.map(tz => <option key={tz} value={tz}>{tz}</option>)}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Observacoes internas</label>
                <textarea value={form.internalNotes} onChange={(e) => updateField("internalNotes", e.target.value)}
                  className={inputClass + " resize-none"} rows={5} placeholder="Notas internas sobre esta empresa..." maxLength={5000} />
                <div className="text-[10px] text-gray-400 mt-1">{form.internalNotes.length}/5000</div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between pt-2">
          <div>
            {step > 1 && (
              <button type="button" onClick={handlePrev}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">
                <ArrowLeft size={14} /> Anterior
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => history.push(backPath)}
              className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-xs font-semibold text-gray-700 dark:text-gray-300 rounded-xl">
              Cancelar
            </button>
            {step < STEPS.length ? (
                <button type="button" onClick={handleNext}
                  className="flex items-center gap-2 px-5 py-2 bg-purple-600 hover:bg-purple-700 text-xs font-bold text-white rounded-xl shadow-lg transition-all">
                  Proximo <ArrowRight size={14} />
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={saving}
                  className="px-5 py-2 create-event-btn text-xs font-bold text-white rounded-xl shadow-purple-glow flex items-center gap-1.5">
                  <Save size={14} /> {saving ? "Salvando..." : submitLabel}
                </button>
              )}

          </div>
        </div>
      </form>
    </div>
  );
};

export default CompanyWizard;
export { TIMEZONES, STATES };
